<script setup lang="ts">
import turfBbox from '@turf/bbox'
import { watchOnce } from '@vueuse/core'
import type { Polygon } from 'geojson'
import { computed, inject, onBeforeUnmount, ref, shallowRef, watch } from 'vue'

import { useFifaGisMap } from '../use-fifa-gis-map'

export interface PolygonStyle {
  fillColor: string
  fillOpacity: number
  strokeColor: string
  strokeOpacity: number
  strokeWidth: number
}

interface Props {
  polygon: Polygon
  editable?: boolean
  focusOnShow?: boolean
  polygonStyle?: Partial<PolygonStyle>
}

const props = withDefaults(defineProps<Props>(), {
  focusOnShow: false,
  editable: false,
})

interface Emits {
  (e: 'update:polygon', polygon: any): void
}

const emit = defineEmits<Emits>()

defineExpose({
  fitPolygonIntoView,
})

const map = inject('fifaGisMap', ref(null))
const isMapLoaded = inject('fifaGisMapLoaded', ref(false))

const { preparePolygon } = useFifaGisMap()

const instance = shallowRef(null)

const isReady = computed(() => map.value && isMapLoaded.value)

watchOnce(
  isReady,
  () => {
    if (props.focusOnShow) {
      fitPolygonIntoView(props.polygon)
    }
  },
  {
    immediate: true,
  },
)

function fitPolygonIntoView(polygon: Polygon) {
  if (polygon?.type !== 'Polygon') return

  if (map.value) {
    const bbox = turfBbox(polygon)
    const bounds = {
      ne: { lat: parseFloat(bbox[1].toString()), lng: parseFloat(bbox[0].toString()) },
      sw: { lat: parseFloat(bbox[3].toString()), lng: parseFloat(bbox[2].toString()) },
    }

    map.value.fitBounds(bounds, { padding: [20, 20], duration: 0.2 })
  }
}

watch(
  () => props.editable,
  () => {
    if (instance.value) {
      instance.value.managePolygonEditMode(props.editable)
    }
  },
)

watch(
  [isReady, () => props.polygon, () => props.polygonStyle],
  () => {
    if (!isReady.value) return

    if (props.polygon?.type !== 'Polygon' && instance.value) {
      instance.value.dispose()
      return
    }

    if (instance.value) {
      instance.value.update(preparePolygon(props.polygon, props.polygonStyle))
      // workaround for the issue with the edit event not being triggered after the update
      instance.value.addEventListener('edit', (_polygon: any) => {
        // workaround for polygon from params not having the getGeometry method
        emit('update:polygon', instance.value.getGeometry())
      })
    } else {
      instance.value = map.value.addPolygon(preparePolygon(props.polygon, props.polygonStyle))
      instance.value.addEventListener('edit', (_polygon: any) => {
        emit('update:polygon', instance.value.getGeometry())
      })
    }
    if (props.editable) {
      instance.value.managePolygonEditMode(props.editable)
    }
  },
  {
    immediate: true,
  },
)

onBeforeUnmount(() => {
  if (instance.value) {
    instance.value.dispose()
    map.value.removePolygon(instance.value)
  }
})
</script>

<template lang="pug"></template>
